import { useInvitePlayerMutation, useReportUserMutation, useBlockUserMutation, useWhisperMutation } from '@http';
import { PlayerInfo } from '@components/helpers/PlayerInfo';
import { useChatStore, useAppStore } from '@stores';
import { Button } from '@components/ui/Button';
import { useTranslation } from 'react-i18next';
import { Modal } from '@components/ui/Modal';
import { toast } from 'react-toastify';
import { useState } from 'react';

interface PlayerModalProps {
  id: string;
  disabled?: boolean;
  children?: React.ReactNode;
}

export function PlayerModal({ id: playerId, disabled, children }: PlayerModalProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { t } = useTranslation(undefined, { keyPrefix: 'Chat.Player' });
  const { mutateAsync: inviteAsync } = useInvitePlayerMutation();
  const { mutateAsync: reportAsync } = useReportUserMutation();
  const { mutateAsync: blockAsync } = useBlockUserMutation();
  const { mutateAsync: whisperAsync } = useWhisperMutation();
  const { addChannels, setActiveChannelId } = useChatStore();

  const { id, refetch } = useAppStore();

  const close = () => setIsOpen(false);

  const report = async () => {
    const data = await reportAsync({ id: playerId });
    if (data?.success) {
      toast(t('reported'), { type: 'success' });
      refetch();
    }
    close();
  };

  const block = async () => {
    const data = await blockAsync({ id: playerId });
    if (data?.success) {
      toast(t('blocked'), { type: 'success' });
      refetch();
    }
    close();
  };

  const whisper = async () => {
    const data = await whisperAsync({ id: playerId });
    if (data && data.id) {
      addChannels([data]);
      setActiveChannelId(data.id);
    }
    close();
  };

  const invite = async () => {
    const data = await inviteAsync({ id: playerId });
    if (!data?.success) {
      toast(t('playerOffline'), { type: 'error' });
    }
    close();
  };

  if (id === playerId || disabled) return children;

  return (
    <>
      <span onClick={() => setIsOpen(true)}>{children}</span>
      <Modal onRequestClose={close} title={t('title')} isOpen={isOpen}>
        <PlayerInfo playerId={playerId} />

        <div className="border-b border-secondary p-2 pt-0">
          <Button onClick={whisper}>{t('whisper')}</Button>
          <Button onClick={invite} className="my-2">{t('invite')}</Button>
          <Button className="mb-2 btn-danger" onClick={block}>{t('block')}</Button>
          <Button className="btn-danger" onClick={report}>{t('report')}</Button>
        </div>
      </Modal>
    </>
  );
}